import Button from "@material-ui/core/Button";
import CheckoutAddDetails, {
  CheckoutAddDetailsPageTab,
  CheckoutAddFormData
} from "@saleor/checkoutAdd/components/CheckoutAddDetails";
import {
  useAddPopupCreateDefaultMutation,
  useAddPopupReorderVariants,
  useAddPopupUpdateMutation,
  useAddPopupVariantsMutation,
  useAddPopupVariantsRemoveMutation
} from "@saleor/checkoutAdd/mutations";
import { useAddPopupQuery } from "@saleor/checkoutAdd/queries";
import {
  CheckoutAddDialog,
  checkoutAddUrl,
  CheckoutAddUrlQueryParams
} from "@saleor/checkoutAdd/urls";
import AssignVariantDialog from "@saleor/components/AssignVariantDialog";
import { DEFAULT_INITIAL_SEARCH_DATA, PAGINATE_BY } from "@saleor/config";
import { configurationMenuUrl } from "@saleor/configuration";
import useBulkActions from "@saleor/hooks/useBulkActions";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
// import usePaginator from "@saleor/hooks/usePaginator";
import { createPaginationState } from "@saleor/hooks/usePaginator";
// import { maybe } from "@saleor/misc"
import { productVariantEditUrl } from "@saleor/products/urls";
import useProductVariantSearch from "@saleor/searches/useProductVariantSearch";
import { ReorderEvent } from "@saleor/types";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import { move } from "@saleor/utils/lists";
import { mapEdgesToItems } from "@saleor/utils/maps";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
export interface CheckoutAddProps {
  params: CheckoutAddUrlQueryParams;
}
const CheckoutAdd: React.FC<CheckoutAddProps> = ({ params }) => {
  const { data, loading, refetch } = useAddPopupQuery({});
  const navigate = useNavigator();
  const intl = useIntl();
  const notify = useNotifier();
  const id = data?.addpopup?.id;
  // const paginate = usePaginator();
  const paginationState = createPaginationState(PAGINATE_BY, params);
  const { isSelected, listElements, reset, toggle, toggleAll } = useBulkActions(
    params.ids
  );
  const [defaultCreate] = useAddPopupCreateDefaultMutation({
    onCompleted: data => {
      if (data.addpopupCreateDefault.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Default Popup Created"
          })
        });
        refetch();
      }
    }
  });

  const [openModal, closeModal] = createDialogActionHandlers<
    CheckoutAddDialog,
    CheckoutAddUrlQueryParams
  >(navigate, params => checkoutAddUrl(params), params);

  // const tabPageInfo =
  //   params.activeTab ===
  //   CheckoutAddDetailsPageTab.variants
  //     ? maybe(() => data?.addPopup.addPopupVariants.pageInfo)
  //     : maybe(() => data?.addPopup.addPopupVariants.pageInfo)

  // const {
  //     loadNextPage,
  //     loadPreviousPage,
  //     pageInfo
  // } = paginate(
  //     tabPageInfo,
  //     paginationState,
  //     params
  // );

  const [addPopupUpdate, addPopupUpdateOpts] = useAddPopupUpdateMutation({
    onCompleted: data => {
      if (data.addpopupUpdate.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "General Information Update Success"
          })
        });
        refetch();
      }
    }
  });

  const handleSubmit = async (data: CheckoutAddFormData) => {
    const result = await addPopupUpdate({
      variables: {
        id,
        input: {
          name: data.name,
          addLink: data.addLink,
          addPcImage: data.addPcImage,
          addMbImage: data.addMbImage,
          enable: data.enable
        }
      }
    });

    return result.data.addpopupUpdate.errors;
  };

  const changeTab = (tab: CheckoutAddDetailsPageTab) => {
    reset();
    navigate(
      checkoutAddUrl({
        activeTab: tab
      })
    );
  };

  const [addPopupVariantsRemove] = useAddPopupVariantsRemoveMutation({
    onCompleted: data => {
      if (data.addpopupRemoveVariants.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Remove AddPopup Variant Success"
          })
        });
        refetch();
      }
    }
  });

  const handleVariantsUnassign = (ids: string[]) =>
    addPopupVariantsRemove({
      variables: {
        ...paginationState,
        id,
        input: {
          variants: ids
        }
      }
    });

  const {
    loadMore: loadMoreProductVariants,
    search: searchProductVariants,
    result: searchProductVariantsOpts
  } = useProductVariantSearch({
    variables: DEFAULT_INITIAL_SEARCH_DATA
  });

  const [addPopupVariants, addPopupVariantsOpts] = useAddPopupVariantsMutation({
    onCompleted: data => {
      if (data.addpopupVariants.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Remove AddPopup Variant Success"
          })
        });
        refetch();
        closeModal();
      }
    }
  });
  const [addPopupReorderVariants] = useAddPopupReorderVariants({
    onCompleted: data => {
      if (data.addpopupReorderVariants.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Reorder AddPopup Variant Success"
          })
        });
        refetch();
      }
    }
  });
  const handleVariantReorder = ({ newIndex, oldIndex }: ReorderEvent) =>
    addPopupReorderVariants({
      optimisticResponse: {
        addpopupReorderVariants: {
          __typename: "AddPopupReorderVariants",
          addpopup: {
            ...data.addpopup,
            addpopupVariants: move(
              data.addpopup.addpopupVariants[oldIndex],
              data.addpopup.addpopupVariants,
              (a, b) => a.id === b.id,
              newIndex
            )
          },
          errors: []
        }
      },
      variables: {
        addpopupId: id,
        move: {
          addpopupVariantId: data.addpopup.addpopupVariants[oldIndex].id,
          sortOrder: newIndex - oldIndex
        }
      }
    });

  return (
    <>
      <CheckoutAddDetails
        checkoutAdd={data?.addpopup}
        onVariantReorder={handleVariantReorder}
        defaultCreate={() => defaultCreate({})}
        disabled={loading}
        onNextPage={null}
        onPreviousPage={null}
        toggle={toggle}
        toggleAll={toggleAll}
        isChecked={isSelected}
        selected={listElements.length}
        onSubmit={handleSubmit}
        onTabClick={changeTab}
        activeTab={params.activeTab}
        onBack={() => navigate(configurationMenuUrl)}
        saveButtonBarState={addPopupUpdateOpts.status}
        variantListToolbar={
          <Button
            color="primary"
            onClick={() =>
              openModal("unassign-variant", {
                ids: listElements
              })
            }
          >
            <FormattedMessage
              defaultMessage="Unassign"
              description="unassign product sku from checkoutAdd, button"
              id="checkoutAddDetailsUnassignVariant"
            />
          </Button>
        }
        onVariantAssign={() => openModal("assign-variant")}
        onVariantUnassign={variantId => handleVariantsUnassign([variantId])}
        onVariantClick={(productId, variantId) => () =>
          navigate(productVariantEditUrl(productId, variantId))}
      />
      <AssignVariantDialog
        confirmButtonState={addPopupVariantsOpts.status}
        hasMore={searchProductVariantsOpts.data?.search?.pageInfo.hasNextPage}
        open={params.action === "assign-variant"}
        onFetch={searchProductVariants}
        onFetchMore={loadMoreProductVariants}
        loading={searchProductVariantsOpts.loading}
        onClose={closeModal}
        onSubmit={variants =>
          addPopupVariants({
            variables: {
              ...paginationState,
              id,
              input: {
                variants: variants.map(variant => variant.id)
              }
            }
          })
        }
        variants={mapEdgesToItems(
          searchProductVariantsOpts?.data?.search
        )?.filter(suggestedVariant => suggestedVariant.id)}
      />
    </>
  );
};
export default CheckoutAdd;
